import type { AffiliateCategory, AffiliateProvider, TrackPlacement } from "analytics/eventCatalog";
import useUser from "components/common/useUser";
import { StipplS, useGuideUi } from "components/discover/discoverSections";
import Icon from "components/Icons/Icon";
import trackEvent from "components/tracking/trackEvent";
// The hotel widget renders on /discover guide pages, served from the marketing entry (no
// Redux, no snackbar provider). `planGate` owns the lazy Redux + Snackbar + LoginModal
// stack those pages need — reuse it here rather than a second copy.
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import dayjs from "dayjs";
import GetYourGuideLogo from "img/partners/getyourtour_logo.png";
import ViatorLogo from "img/partners/viator_logo.png";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { DayPickerRangeController, DayPickerSingleDateController } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { useTranslation } from "react-i18next";
import { getPartnerUrl, PARTNERS } from "utils/parners";
import { buildStayClickoutUrl } from "utils/urlUtils";
import "../../../css/react_dates_overrides.scss";
import styles from "./TravelWidget.module.scss";

export type HotelPreview = { name: string; area: string; photo: string; tag: string; bookingUrl?: string };

// ── Hardcoded review meta for the preview cards ───────────────────────────────
// The curated hotel data carries no ratings or review counts (and its `tag` is a
// free-text label like "Local favourite"), so there is nothing to derive from.
// These three are illustrative placeholders, cycled by card position. The first
// entry is the highest, so card 0 gets the "Best rated" badge.
const HOTEL_CARD_META: { rating: string; label: string; reviews: string }[] = [
	{ rating: "9.4", label: "Exceptional", reviews: "2,318" },
	{ rating: "8.9", label: "Excellent", reviews: "1,204" },
	{ rating: "8.6", label: "Very good", reviews: "876" },
];

// Every outbound link in this widget is an affiliate clickout, and none of them reported one.
// `affiliate_clicked` is the canonical event — MainCard and TransportActionButton both fire it,
// and trackEvent.ts hangs `recordClickout()` off it — so a Booking.com click from a /discover hub
// was earning money and leaving no trace, while the same click from a trip card was counted.
// See work/discover-analytics-coverage.md.
//
// `url_source: "fallback"` because these URLs come from `getPartnerUrl()`, our own affiliate
// builder, rather than the Vio API. Stated rather than omitted: the distinction only works if
// every affiliate event carries it, otherwise "missing" and "fallback" are indistinguishable.
const PROVIDER_SLUG: Record<string, AffiliateProvider> = {
	"Booking.com": "booking_com",
	Hostelworld: "hostelworld",
	GetYourGuide: "getyourguide",
	Viator: "viator",
};

const trackClickout = (providerName: keyof typeof PROVIDER_SLUG | string, category: AffiliateCategory, placement: TrackPlacement) => {
	const provider = PROVIDER_SLUG[providerName];
	if (!provider) return;
	try {
		trackEvent("affiliate_clicked", { provider, category, placement, url_source: "fallback" }, "discover");
	} catch {
		// tracking must never affect a clickout
	}
};

type Props = {
	variant?: "hotels" | "tours";
	destination?: string;
	/** English country name, forwarded to the in-app stay search as `country`. */
	country?: string;
	/** Geo centre — when both are set, the hotel-widget primary CTA / "See all" open the in-app stay search seeded with these. */
	lat?: number;
	lng?: number;
	defaultStartDate?: string;
	defaultEndDate?: string;
	toursUrl?: string;
	bookingUrl?: string;
	avgStayDays?: number;
	title?: string;
	hotels?: HotelPreview[];
	featurePhoto?: string;
	featureLabel?: string;
};

const TravelWidget = ({
	variant = "hotels",
	destination = "",
	country,
	lat,
	lng,
	defaultStartDate,
	defaultEndDate,
	toursUrl,
	bookingUrl,
	avgStayDays = 3,
	title,
	hotels = [],
	featurePhoto,
	featureLabel,
}: Props) => {
	const { t } = useTranslation(["dashboard"]);
	// The marketing bundle (`/discover/*` guide pages, `/{lang}/blog/*` articles) never
	// initialises react-i18next, so `t` there returns the key untouched. Those pages instead
	// provide the resolved locale dictionary through `GuideUiContext` —
	// `getBlogTranslations(article.locale)` in the blog renderers — which `useGuideUi` reads.
	const guideUi = useGuideUi();

	/**
	 * Resolution order: react-i18next (dashboard bundle) → `GuideUiContext` dictionary
	 * (marketing/blog bundle) → the English literal passed as `fallback`. react-i18next returns
	 * the key unchanged on a miss; `useGuideUi` returns its `fallback` argument on a miss. Real
	 * translations still win wherever either source has the key, so a reader on `/nl/blog/...`
	 * now gets the widget in their language instead of the English fallbacks.
	 *
	 * English fallbacks rather than initialising i18next in the marketing entry: that would pull
	 * all three locale namespaces into the bundle every homepage visitor downloads, which is the
	 * cost `guideRoutes.tsx` was created to avoid.
	 *
	 * Fallbacks below are written with `{{token}}`; the blog copy files use `{token}` — fill both.
	 */
	const tr = (key: string, fallback: string, vars?: Record<string, string>): string => {
		// `t` is typed as `string | object` because i18next can return a tree; every key here
		// is a leaf string, and a non-string means the lookup missed anyway.
		const fromI18next = t(key, vars as any) as unknown;
		if (typeof fromI18next === "string" && fromI18next && fromI18next !== key) return fromI18next;

		const fromGuide = guideUi(key, fallback);
		const resolved = fromGuide && fromGuide !== fallback ? fromGuide : fallback;

		return vars ? Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{{${k}}}`, v).replace(`{${k}}`, v), resolved) : resolved;
	};
	const { user } = useUser();
	// Reused marketing-safe auth prompt (lazy Redux + Snackbar + LoginModal). `"modal"` so a
	// phone still gets the form here — the destination is a web page, not the app.
	const stayGate = usePlanGate("discover_stay_search", "modal");
	const pendingStaySearchRef = useRef(false);

	const defaultStart = defaultStartDate || dayjs().add(30, "day").format("YYYY-MM-DD");
	const defaultEnd =
		defaultEndDate ||
		dayjs(defaultStart)
			.add(avgStayDays || 3, "day")
			.format("YYYY-MM-DD");

	const [startMoment, setStartMoment] = useState(() => moment(defaultStart));
	const [endMoment, setEndMoment] = useState<moment.Moment | null>(() => moment(defaultEnd));
	const [focusedInput, setFocusedInput] = useState<"startDate" | "endDate">("startDate");
	const [calendarOpen, setCalendarOpen] = useState(false);
	const [openAbove, setOpenAbove] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const startDate = startMoment.format("YYYY-MM-DD");
	const endDate =
		endMoment?.format("YYYY-MM-DD") ??
		startMoment
			.clone()
			.add(avgStayDays || 3, "day")
			.format("YYYY-MM-DD");

	const formatDateDisplay = (m: moment.Moment) => m.format("D MMM");

	// Hotel-widget date fields: a filled field shows the date; an empty one shows a small
	// brand dot + "Select date", mirroring the redesign's StayDatePicker placeholder state.
	const renderHotelDateValue = (m: moment.Moment | null) =>
		m ? (
			formatDateDisplay(m)
		) : (
			<span className={styles.hotelDatePlaceholder}>
				<span className={styles.hotelDateFieldDot} aria-hidden />
				{tr("explore.widget.selectDate", "Select date")}
			</span>
		);

	const isHotels = variant === "hotels";
	const clickoutCategory: AffiliateCategory = isHotels ? "accommodation" : "tour";
	const primaryProvider = isHotels ? "Booking.com" : "GetYourGuide";
	const secondaryProvider = isHotels ? "Booking.com" : "Viator";

	const primaryUrl = isHotels
		? bookingUrl || getPartnerUrl("Booking.com", destination, startDate, endDate, user?.username)
		: toursUrl || getPartnerUrl("GetYourGuide", destination, startDate, endDate, user?.username);

	/**
	 * The Booking.com link for ONE named hotel, rather than the destination.
	 *
	 * Every row used to carry `primaryUrl`, so all three picks on a page sent the visitor to the
	 * same city-level search. `buildStayClickoutUrl` already solved this for in-plan stays and
	 * says why: dropping someone on a results page for a hotel they have already chosen is the
	 * friction that sends them to booking.com directly instead of through us.
	 *
	 * No `url` argument on purpose. The destination's `bookingUrl` is a search for the CITY, and
	 * handing it in would make every hotel resolve back to exactly what this replaces. No `ctx`
	 * either: that labels a clickout with the trip and place it came from, and a curated pick on
	 * a public guide page has neither.
	 */
	const hotelClickoutUrl = (hotel: HotelPreview) =>
		buildStayClickoutUrl({
			// The resolved Booking.com property page when the knowledge base has one — 227 of the
			// 321 curated hotels, resolved at build time and committed as data. buildStayClickoutUrl
			// recognises a /hotel/ URL and stamps the dates and the affiliate label onto it instead
			// of searching. The other 94 fall through to the name search below, deliberately: they
			// are the matches that were not confident enough to trust with a direct link.
			url: hotel.bookingUrl,
			name: hotel.name,
			// The city, but only when the name does not already carry it. 118 of the 321 curated
			// picks are named for their destination ("Park Hyatt Busan", "Aman Tokyo"), where
			// appending it again searches "Park Hyatt Busan Busan". The other 203 need it badly:
			// "Cattleya" and "Rayavadee" are ambiguous worldwide and useless on their own.
			city: hotel.name.toLowerCase().includes((destination || "").toLowerCase()) ? undefined : destination,
			startDate,
			endDate,
			username: user?.username,
		});

	const secondaryUrl = isHotels
		? bookingUrl || getPartnerUrl("Booking.com", destination, startDate, endDate, user?.username)
		: toursUrl || getPartnerUrl("Viator", destination, startDate, endDate, user?.username);

	const bookingParner = PARTNERS.find((item) => item.name === `Booking.com`);
	const hostelworldParner = PARTNERS.find((item) => item.name === `Hostelworld`);
	const hostelworldUrl = getPartnerUrl("Hostelworld", destination, startDate, endDate, user?.username);

	// The hotel-widget primary CTA and its "See all" link open the in-app stay search
	// (`/bookings/search`) seeded with the picked dates — but only when the caller passed
	// coordinates for it (discover guide / itinerary stop). Without them there is nothing to
	// search, so the buttons keep their Booking.com affiliate clickout. Logged-out visitors
	// get the login modal first and land on the search once they're in.
	const canStaySearch = typeof lat === "number" && typeof lng === "number";

	const goToStaySearch = () => {
		// A full navigation, not history.push: this widget renders in the marketing bundle,
		// which has no client route for `/bookings/search` (a push there renders a blank shell).
		// The search rides on the query string — StaySearchPage reads it as a route-state fallback.
		//
		// `z` is not optional, even though it looks like presentation. `useHotelSearchUrlState`'s
		// `parseUrl()` restores the viewport only when lat, lng AND z are all present together, so
		// omitting it left the map with no seeded viewport at all; `MapboxMap`'s `fitMap()` then
		// fell back to its empty-state `DEFAULT_POSITION` world bbox, and `writeUrl()` stamped that
		// fallback back into the URL as if it were real — rewriting the address to lat=8.39164&
		// lng=1.55386 (the bbox centre, a point in the Gulf of Guinea) and dropping city/checkIn/
		// checkOut/guests/country with it, because `writeUrl` rebuilds the query from its own keys
		// only. That is 123yb694h6w: the CTA appeared to navigate, then threw the user into the
		// middle of the ocean and crashed to the error boundary.
		//
		// 12 is city scale — this CTA always comes from a `/discover/<country>/<destination>` page,
		// so the search opens on the destination the reader was just reading about. The user's own
		// pan/zoom overwrites it on the first map move.
		const params = new URLSearchParams({
			city: destination || "",
			lat: String(lat),
			lng: String(lng),
			z: "12",
			checkIn: startDate,
			checkOut: endDate,
			guests: "2",
		});
		if (country) params.set("country", country);
		window.location.assign(`/bookings/search?${params.toString()}`);
	};

	const handleStaySearch = (placement: TrackPlacement) => {
		if (!canStaySearch) {
			trackClickout(primaryProvider, clickoutCategory, placement);
			if (typeof window !== "undefined") window.open(primaryUrl, "_blank", "noopener,noreferrer");
			return;
		}
		if (!user?._id) {
			pendingStaySearchRef.current = true;
			stayGate.requestPlan();
			return;
		}
		goToStaySearch();
	};

	// PlanGateModal closes itself on success; we only need to resume the pending navigation.
	const handleLoginResolved = () => {
		if (pendingStaySearchRef.current) {
			pendingStaySearchRef.current = false;
			goToStaySearch();
		}
	};

	useEffect(() => {
		if (!calendarOpen) return undefined;
		const handleClick = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setCalendarOpen(false);
			}
		};
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setCalendarOpen(false);
		};
		const timer = setTimeout(() => document.addEventListener("mousedown", handleClick), 0);
		document.addEventListener("keydown", handleKey);
		return () => {
			clearTimeout(timer);
			document.removeEventListener("mousedown", handleClick);
			document.removeEventListener("keydown", handleKey);
		};
	}, [calendarOpen]);

	const openCalendar = (focus: "startDate" | "endDate") => {
		if (calendarOpen && focusedInput === focus) {
			setCalendarOpen(false);
			return;
		}
		setFocusedInput(focus);
		if (wrapperRef.current) {
			const rect = wrapperRef.current.getBoundingClientRect();
			const spaceBelow = window.innerHeight - rect.bottom;
			const calendarHeight = isHotels ? 380 : 340;
			setOpenAbove(spaceBelow < calendarHeight);
		}
		setCalendarOpen(true);
	};

	const searchWidget = (
		<div className={styles.widgetWrapper} ref={wrapperRef}>
			{calendarOpen && (
				<div className={`${styles.calendarDropdown} ${openAbove ? styles.calendarDropdownAbove : styles.calendarDropdownBelow}`}>
					{isHotels ? (
						<DayPickerRangeController
							startDate={startMoment}
							endDate={endMoment}
							onDatesChange={({ startDate: sd, endDate: ed }) => {
								if (sd) setStartMoment(sd);
								setEndMoment(ed);
								if (focusedInput === "startDate" && sd) {
									setFocusedInput("endDate");
								} else if (focusedInput === "endDate" && ed) {
									setCalendarOpen(false);
								}
							}}
							focusedInput={focusedInput}
							onFocusChange={(f) => setFocusedInput(f || "startDate")}
							numberOfMonths={2}
							daySize={32}
							firstDayOfWeek={1}
							hideKeyboardShortcutsPanel
							noBorder
							isOutsideRange={(day) => day.isBefore(moment(), "day")}
						/>
					) : (
						<DayPickerSingleDateController
							date={startMoment}
							onDateChange={(date) => {
								if (date) {
									setStartMoment(date);
									setCalendarOpen(false);
								}
							}}
							focused={calendarOpen}
							onFocusChange={() => {}}
							numberOfMonths={1}
							daySize={32}
							firstDayOfWeek={1}
							hideKeyboardShortcutsPanel
							noBorder
							isOutsideRange={(day) => day.isBefore(moment(), "day")}
						/>
					)}
				</div>
			)}

			<div className={styles.widgetContainer}>
				<div className={styles.flexRow}>
					{/* LEFT COLUMN: Date Trigger(s) */}
					<div className={styles.dateColumn}>
						{isHotels ? (
							<>
								<button type="button" className={styles.checkInLabel} onClick={() => openCalendar("startDate")}>
									<span className={styles.labelTitle}>{tr("explore.widget.checkIn", "Check in")}</span>
									<span className={styles.labelValue}>{formatDateDisplay(startMoment)}</span>
								</button>
								<button type="button" className={styles.checkOutLabel} onClick={() => openCalendar("endDate")}>
									<span className={styles.labelTitle}>{tr("explore.widget.checkOut", "Check out")}</span>
									<span className={styles.labelValue}>{endMoment ? formatDateDisplay(endMoment) : "—"}</span>
								</button>
							</>
						) : (
							<button type="button" className={styles.tourDateLabel} onClick={() => openCalendar("startDate")}>
								<span className={styles.labelTitle}>{tr("explore.widget.date", "Date")}</span>
								<span className={styles.labelValue}>{formatDateDisplay(startMoment)}</span>
								<span className={styles.labelSubtitle}>{tr("explore.widget.ticketsTours", "tickets & tours")}</span>
							</button>
						)}
					</div>

					{/* RIGHT COLUMN: Action Buttons */}
					<div className={styles.actionsColumn}>
						<div className={styles.searchButtonWrapper}>
							<a
								href={primaryUrl}
								onClick={() => trackClickout(primaryProvider, clickoutCategory, "card_cta")}
								target="_blank"
								rel="noopener noreferrer"
								className={`${styles.searchButton} ${isHotels ? styles.hotelsPrimary : styles.toursPrimary}`}
							>
								{isHotels ? (
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" className={styles.svgIcon}>
										<circle cx="11" cy="11" r="7" stroke="white" strokeWidth="2" />
										<path d="M16.5 16.5L21 21" stroke="white" strokeWidth="2" strokeLinecap="round" />
									</svg>
								) : (
									<div className={`${styles.logoContainer} ${styles.logoSize22}`}>
										<img src={GetYourGuideLogo} alt={"GetYourGuide"} className={styles.logoImage} />
									</div>
								)}

								<span className={`${styles.searchButtonText} ${!isHotels ? styles.toursButtonTextSize : ""}`}>
									{isHotels
										? tr("explore.widget.findStayIn", "Find stay in {{name}}", { name: destination ?? "" })
										: tr("explore.widget.findTours", "Find tours")}
								</span>

								<svg width="12" height="12" viewBox="0 0 18 18" fill="none" className={styles.svgIcon}>
									<path d="M3.5 9h11M10 4.5l4.5 4.5-4.5 4.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</a>
						</div>

						<div className={styles.providerRow}>
							<a
								href={secondaryUrl}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.providerLink}
								onClick={() => trackClickout(secondaryProvider, clickoutCategory, "inline_banner")}
							>
								<div className={`${styles.logoContainer} ${isHotels ? styles.logoSize20 : styles.logoSize22}`}>
									<img src={isHotels ? bookingParner?.logo : ViatorLogo} alt={isHotels ? bookingParner?.name : "Viator"} className={styles.logoImage} />
								</div>

								<span className={`${styles.providerName} ${!isHotels ? styles.providerNameFontRubik : ""}`}>
									{isHotels ? "Booking.com" : tr("explore.widget.findToursViator", "Find tours on Viator")}
								</span>

								<svg width="12" height="12" viewBox="0 0 18 18" fill="none" className={styles.svgIcon}>
									<path d="M3.5 9h11M10 4.5l4.5 4.5-4.5 4.5" stroke="#8aabb5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	if (!hotels?.length) return searchWidget;

	const mockCalendar = (
		<DayPickerRangeController
			startDate={startMoment}
			endDate={endMoment}
			onDatesChange={({ startDate: sd, endDate: ed }) => {
				if (sd) setStartMoment(sd);
				setEndMoment(ed);
				if (focusedInput === "startDate" && sd) {
					setFocusedInput("endDate");
				} else if (focusedInput === "endDate" && ed) {
					setCalendarOpen(false);
				}
			}}
			focusedInput={focusedInput}
			onFocusChange={(f) => setFocusedInput(f || "startDate")}
			numberOfMonths={1}
			daySize={34}
			firstDayOfWeek={1}
			hideKeyboardShortcutsPanel
			noBorder
			isOutsideRange={(day) => day.isBefore(moment(), "day")}
		/>
	);

	return (
		<div className={styles.hotelWidget}>
			<div className={styles.hotelWidgetHeader}>
				<span className={styles.hotelWidgetTitleWrap}>
					<span className={styles.hotelWidgetTitleIcon}>
						<Icon name="bed--chunky" size={16} fill="currentColor" />
					</span>
					<span className={styles.hotelWidgetTitle}>{title}</span>
				</span>
				<button type="button" className={styles.hotelWidgetSeeAll} onClick={() => handleStaySearch("header_cta")}>
					{tr("explore.widget.seeAll", "See all")}
					<Icon name="lineArrowRight" size={12} fill="currentColor" />
				</button>
			</div>

			{/* Date bar */}
			<div className={styles.hotelDateBar} ref={wrapperRef}>
				<div className={styles.hotelDateBarField}>
					<button type="button" className={styles.hotelDateBarInner} onClick={() => openCalendar("startDate")}>
						<span className={styles.hotelDateField}>
							<span className={styles.hotelDateFieldLabel}>{tr("explore.widget.checkIn", "Check in")}</span>
							<span className={styles.hotelDateFieldValue}>{renderHotelDateValue(startMoment)}</span>
						</span>
					</button>
					{calendarOpen && focusedInput === "startDate" && (
						<div className={`${styles.calendarDropdown} ${openAbove ? styles.calendarDropdownAbove : styles.calendarDropdownBelow}`}>{mockCalendar}</div>
					)}
				</div>
				<span className={styles.hotelDateDivider} />
				<div className={styles.hotelDateBarField}>
					<button type="button" className={styles.hotelDateBarInner} onClick={() => openCalendar("endDate")}>
						<span className={styles.hotelDateField}>
							<span className={styles.hotelDateFieldLabel}>{tr("explore.widget.checkOut", "Check out")}</span>
							<span className={styles.hotelDateFieldValue}>{renderHotelDateValue(endMoment)}</span>
						</span>
					</button>
					{calendarOpen && focusedInput === "endDate" && (
						<div
							className={`${styles.calendarDropdown} ${styles.calendarDropdownRight} ${openAbove ? styles.calendarDropdownAbove : styles.calendarDropdownBelow}`}
						>
							{mockCalendar}
						</div>
					)}
				</div>
				<span className={styles.hotelDateCalBtn} aria-hidden>
					<Icon name="calendarLucide" size={17} stroke="#fff" />
				</span>
			</div>

			{!!hotels?.length && (
				<div className={styles.hotelGrid}>
					{hotels.map((h, i) => {
						const m = HOTEL_CARD_META[i % HOTEL_CARD_META.length];
						return (
							<a
								key={h.name}
								href={hotelClickoutUrl(h)}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.hotelCard}
								onClick={() => trackClickout(primaryProvider, clickoutCategory, "list_card")}
							>
								<span className={styles.hotelCardPhoto}>
									<img src={h.photo} alt={h.name} className={styles.hotelCardPhotoImg} />
									{i === 0 && <span className={styles.hotelCardBadge}>Best rated</span>}
								</span>
								<span className={styles.hotelCardName}>{h.name}</span>
								<span className={styles.hotelCardRatingRow}>
									<span className={styles.hotelCardRating}>{m.rating}</span>
									<span className={styles.hotelCardRatingMeta}>
										{m.label} · {m.reviews} reviews
									</span>
								</span>
								<span className={styles.hotelCardFooter}>
									{!!h.area ? (
										<span className={styles.hotelCardLocation}>
											<Icon name="marker--outlined" size={12} fill="currentColor" />
											<span className={styles.hotelCardArea}>{h.area}</span>
										</span>
									) : (
										<div />
									)}
									<span className={styles.hotelCardArrow}>
										<Icon name="lineArrowRight" size={14} fill="currentColor" />
									</span>
								</span>
							</a>
						);
					})}
				</div>
			)}
			{/* {featurePhoto && (
				<a
					href={primaryUrl}
					target="_blank"
					rel="noopener noreferrer"
					className={styles.hotelFeatureCard}
					onClick={() => trackClickout(primaryProvider, clickoutCategory, "carousel_card")}
				>
					<img src={featurePhoto} alt="" className={styles.hotelFeatureImg} />
					<span className={styles.hotelFeatureOverlay} />
					<span className={styles.hotelFeatureContent}>
						<span className={styles.hotelFeatureLabel}>{featureLabel}</span>
						<span className={styles.hotelFeatureArrow}>
							<Icon name="lineArrowRight" size={14} fill="currentColor" />
						</span>
					</span>
				</a>
			)} */}

			{/* Primary CTA — opens the in-app stay search (login first if needed) */}
			<button
				type="button"
				onClick={() => handleStaySearch("card_cta")}
				className={`${styles.searchButton} ${styles.hotelsPrimary} ${styles.hotelWidgetPrimaryCta}`}
			>
				<StipplS size={14} color="#fff" />
				<span className={styles.hotelWidgetPrimaryCtaText}>{tr("explore.widget.searchHotelsOnStippl", "Search hotels on Stippl")}</span>
				<Icon name="chevron-right" size={8} fill="#fff" />
			</button>

			{/* Secondary provider clickouts */}
			<div className={styles.hotelWidgetProviderRow}>
				<a
					href={secondaryUrl}
					target="_blank"
					rel="noopener noreferrer"
					className={styles.hotelWidgetProviderLink}
					onClick={() => trackClickout(secondaryProvider, clickoutCategory, "inline_banner")}
				>
					<div className={`${styles.logoContainer} ${styles.logoSize20}`}>
						<img src={bookingParner?.logo} alt={bookingParner?.name} className={styles.logoImage} />
					</div>
					<span className={styles.hotelWidgetProviderName}>{tr("explore.widget.checkAvailableHotels", "Check available hotels")}</span>
					<Icon name="chevron-right" size={8} fill="#8aabb5" />
				</a>
				<a
					href={hostelworldUrl}
					target="_blank"
					rel="noopener noreferrer"
					className={styles.hotelWidgetProviderLink}
					onClick={() => trackClickout("Hostelworld", "accommodation", "inline_banner")}
				>
					<div className={`${styles.logoContainer} ${styles.logoSize20}`}>
						<img src={hostelworldParner?.logo} alt={hostelworldParner?.name} className={styles.logoImage} />
					</div>
					<span className={styles.hotelWidgetProviderName}>{tr("explore.widget.checkAvailableHostels", "Check available hostels")}</span>
					<Icon name="chevron-right" size={8} fill="#8aabb5" />
				</a>
			</div>

			<PlanGateModal
				gate={stayGate}
				title={tr("explore.widget.searchHotelsOnStippl", "Search hotels on Stippl")}
				loginTitle={tr("explore.widget.searchHotelsOnStippl", "Search hotels on Stippl")}
				initialView="login"
				allowMobile
				onLogin={handleLoginResolved}
			/>
		</div>
	);
};

export default TravelWidget;
